import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import api from "@/lib/api";
import { Steps } from "@/pages/OrderVerify";
import Seo from "@/components/Seo";

const STAGES = {
  paying: "Confirming your payment…",
  in_queue: "Your report is in the queue…",
  processing: "Generating your roof measurements…",
  complete: "Your report is ready!",
  failed: "Something went wrong with this order.",
};

export default function OrderProcessing() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const reportId = params.get("report_id");
  const sessionId = params.get("session_id");
  const [stage, setStage] = useState("paying");
  const [error, setError] = useState(null);
  const polling = useRef(false);
  const stageRef = useRef(stage);
  useEffect(() => { stageRef.current = stage; }, [stage]);

  useEffect(() => {
    if (!reportId) { navigate("/dashboard", { replace: true }); return; }
    sessionStorage.removeItem("mtr_order_address");
    sessionStorage.removeItem("mtr_order_product");
    let cancelled = false;
    let attempts = 0;

    async function tick() {
      if (cancelled || polling.current) return;
      polling.current = true;
      try {
        // 1) Confirm payment first
        if (stageRef.current === "paying" && sessionId) {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            setStage("in_queue");
          } else if (data.status === "expired" || attempts > 30) {
            setError("We couldn't confirm your payment. If you were charged, please contact support.");
            polling.current = false;
            return;
          }
        } else {
          // 2) Poll report status
          const { data } = await api.get(`/reports/${reportId}`);
          if (data.status === "complete") {
            setStage("complete");
            setTimeout(() => navigate(`/reports/${reportId}`, { replace: true }), 1200);
            polling.current = false;
            return;
          } else if (data.status === "failed") {
            setStage("failed");
            polling.current = false;
            return;
          } else if (data.status === "processing") {
            setStage("processing");
          } else if (data.status === "in_queue") {
            setStage("in_queue");
          }
        }
      } catch {
        // keep trying
      } finally {
        attempts += 1;
        polling.current = false;
      }
    }

    const interval = setInterval(tick, 3000);
    tick();
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, sessionId]);

  const done = stage === "complete";
  const failed = stage === "failed" || error;

  return (
    <div className="container-mtr py-8 sm:py-12 max-w-2xl" data-testid="order-processing-page">
      <Seo title="Processing your report — MeasureTheRoof" description="Your roof measurement report is being generated." path="/order/processing" noindex />
      <Steps current={3} />

      <div className="card-mtr mt-6 text-center py-10">
        {failed ? (
          <div data-testid="processing-failed">
            <div className="w-16 h-16 rounded-full bg-[#FDECEA] grid place-items-center mx-auto mb-4">
              <AlertCircle className="text-caution-red" size={32} />
            </div>
            <h1 className="font-body font-extrabold text-navy text-[22px]">{error || STAGES.failed}</h1>
            <p className="text-mortar text-[14px] mt-2 max-w-sm mx-auto">
              Please contact <a href="mailto:support@measuretheroof.com" className="text-orange font-semibold">support@measuretheroof.com</a> and we'll make it right.
            </p>
            <Link to="/dashboard" className="btn-ghost mt-6 inline-flex" data-testid="processing-back-dashboard">Back to dashboard</Link>
          </div>
        ) : done ? (
          <div data-testid="processing-complete">
            <div className="w-16 h-16 rounded-full bg-[#E6F4EA] grid place-items-center mx-auto mb-4">
              <CheckCircle2 className="text-go-green" size={32} />
            </div>
            <h1 className="font-body font-extrabold text-navy text-[22px]">{STAGES.complete}</h1>
            <p className="text-mortar text-[14px] mt-2">Taking you to your measurements…</p>
          </div>
        ) : (
          <div data-testid="processing-active">
            <div className="w-16 h-16 rounded-full bg-[#E3F0FF] grid place-items-center mx-auto mb-4">
              <Loader2 className="text-sky-blue animate-spin" size={32} />
            </div>
            <h1 className="font-body font-extrabold text-navy text-[22px]">{STAGES[stage]}</h1>
            <p className="text-mortar text-[14px] mt-2 max-w-sm mx-auto flex items-center justify-center gap-1.5">
              <Clock size={14} /> This usually takes a few minutes, but can take up to a few hours. You can leave this page — we'll email you when it's ready.
            </p>
            <Link to="/reports" className="btn-ghost mt-6 inline-flex" data-testid="processing-view-reports">View all reports</Link>
          </div>
        )}
      </div>
    </div>
  );
}
